import { ContentType } from '../../enums/ContentType';
import { Language } from '../../enums/Language';
import { CategoryModel, MetadataModel } from '../../models';
import { MultiLanguageTextModel } from '../../models/MultiLanguageTextModel';
import { Tag } from '../../types/Tag';
import MetadataRepository from '../MetadataRepository';
import { apiFetch } from '../../utils/apiFetch';

interface V2Endpoints {
  categories?: string;
  tags?: string;
}

interface CategoryItemResponse {
  slug: string;
  name_en?: string;
  name_de?: string;
  name_it?: string;
  name_fr?: string;
  name_es?: string;
  name_pt?: string;
  icon?: string;
  text_icon?: string;
  text_icon_literal?: string;
  color?: string;
  sort_order?: number;
  is_system?: boolean;
  children?: CategoryItemResponse[];
}

interface CategoriesApiResponse {
  canonical?: CategoryItemResponse[];
  partner?: CategoryItemResponse[];
}

interface TagItemResponse {
  id: string;
  name: string;
  slug: string;
}

interface CategorizedTagsApiResponse {
  system?: TagItemResponse[];
  user?: TagItemResponse[];
  general?: TagItemResponse[];
  partner?: TagItemResponse[];
}

export default class BeatAPIMetadataRepository extends MetadataRepository {
  private _apiKey;
  private _url;
  private _v2Endpoints: V2Endpoints;

  constructor(url?: string, apiKey?: string, v2Endpoints?: V2Endpoints) {
    super();
    this._apiKey = apiKey || '';
    this._url = url || '';
    this._v2Endpoints = v2Endpoints || {};
  }

  set v2Endpoints(endpoints: V2Endpoints) {
    this._v2Endpoints = endpoints;
  }

  protected async _loadMetadata(): Promise<any> {
    const metaDataResponse = await apiFetch(this._url, {
      apiKey: this._apiKey,
    });
    const data = await metaDataResponse.json();

    // Fetch and merge v2 endpoints if available
    if (this._v2Endpoints.categories || this._v2Endpoints.tags) {
      const [v2Categories, v2Tags] = await Promise.all([
        this._fetchV2Categories(),
        this._fetchV2Tags(),
      ]);

      // Merge v2 data into legacy response
      if (v2Categories) {
        data.data.categories = v2Categories;
      }
      if (v2Tags) {
        data.data.tags = v2Tags;
      }
    }

    return data;
  }

  private async _fetchV2Categories(): Promise<any[] | null> {
    if (!this._v2Endpoints.categories) {
      return null;
    }

    try {
      const response = await apiFetch(this._v2Endpoints.categories, {
        apiKey: this._apiKey,
      });

      if (!response.ok) {
        console.error('[BeatAPIMetadataRepository] Failed to fetch v2/categories:', response.statusText);
        return null;
      }

      const data: CategoriesApiResponse = await response.json();
      return this._mergeCategories(data);
    } catch (error) {
      console.error('[BeatAPIMetadataRepository] Error fetching v2/categories:', error);
      return null;
    }
  }

  private async _fetchV2Tags(): Promise<any[] | null> {
    if (!this._v2Endpoints.tags) {
      return null;
    }

    try {
      const response = await apiFetch(this._v2Endpoints.tags, {
        apiKey: this._apiKey,
      });

      if (!response.ok) {
        console.error('[BeatAPIMetadataRepository] Failed to fetch v2/tags:', response.statusText);
        return null;
      }

      const data: CategorizedTagsApiResponse = await response.json();
      return this._mergeTags(data);
    } catch (error) {
      console.error('[BeatAPIMetadataRepository] Error fetching v2/tags:', error);
      return null;
    }
  }

  private _mergeCategories(data: CategoriesApiResponse): any[] {
    const merged: any[] = [];
    const seen = new Set<string>();

    const transformCategory = (cat: CategoryItemResponse): any => {
      return {
        id: cat.slug,
        slug: cat.slug,
        color: cat.color || '',
        textIcon: cat.text_icon || '',
        textIconLiteral: cat.text_icon_literal || '',
        nameEn: cat.name_en || '',
        nameDe: cat.name_de || '',
        nameIt: cat.name_it || '',
        nameFr: cat.name_fr || '',
        nameEs: cat.name_es || '',
        namePt: cat.name_pt || '',
        icon: cat.icon || '',
        sortOrder: cat.sort_order ?? 0,
        isSystem: cat.is_system ?? false,
        children: (cat.children || []).map(child => transformCategory(child)),
      };
    };

    const addCategory = (cat: CategoryItemResponse): void => {
      const id = cat.slug;
      if (seen.has(id)) return;
      seen.add(id);
      merged.push(transformCategory(cat));
    };

    // Process canonical categories with nested children
    for (const cat of data.canonical || []) {
      addCategory(cat);
    }

    // Then process partner categories with nested children
    for (const cat of data.partner || []) {
      addCategory(cat);
    }

    return merged;
  }

  private _mergeTags(data: CategorizedTagsApiResponse): any[] {
    const merged: any[] = [];
    const seen = new Set<string>();

    const addTags = (tags: TagItemResponse[] | undefined, tagType: string): void => {
      if (!tags) return;
      for (const tag of tags) {
        if (seen.has(tag.id)) continue;
        seen.add(tag.id);
        merged.push({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          tagType,
        });
      }
    };

    // Merge in order: system, general, partner, user
    addTags(data.system, 'system');
    addTags(data.general, 'general');
    addTags(data.partner, 'partner');
    addTags(data.user, 'user');

    return merged;
  }

  private _trimUnicodeSyntax(str: string) {
    if (str.length === 6 && str[0] === '\\' && str[1] === 'u')
      return str.slice(2);
    return str;
  }

  private _toUnicode(str: string) {
    const charHex = this._trimUnicodeSyntax(str);
    const charDec = parseInt(charHex, 16);
    return String.fromCharCode(charDec);
  }

  protected _transform(data: any): MetadataModel {
    const transformCategory = (c: any): CategoryModel => {
      const languageTexts = [
        {
          language: Language.en,
          text: c.nameEn || '',
          contentType: ContentType.TEXT,
        },
        {
          language: Language.de,
          text: c.nameDe || '',
          contentType: ContentType.TEXT,
        },
      ];

      // Add additional languages if present (from v2 endpoint)
      if (c.nameIt) {
        languageTexts.push({
          language: Language.it,
          text: c.nameIt,
          contentType: ContentType.TEXT,
        });
      }
      if (c.nameFr) {
        languageTexts.push({
          language: Language.fr,
          text: c.nameFr,
          contentType: ContentType.TEXT,
        });
      }
      if (c.nameEs) {
        languageTexts.push({
          language: Language.es,
          text: c.nameEs,
          contentType: ContentType.TEXT,
        });
      }
      if (c.namePt) {
        languageTexts.push({
          language: Language.pt,
          text: c.namePt,
          contentType: ContentType.TEXT,
        });
      }

      // Recursively transform children
      const children = (c.children || []).map((child: any) => transformCategory(child));

      return new CategoryModel(
        c.id,
        c.slug || '',
        c.color || '',
        c.textIcon ? this._toUnicode(c.textIcon) : (c.icon || ''),
        new MultiLanguageTextModel(languageTexts),
        undefined,
        c.parent || null,
        c.textIcon,
        c.textIconLiteral,
        c.sortOrder,
        c.isSystem,
        children
      );
    };

    const categories: CategoryModel[] = data.data.categories.map((c: any) => transformCategory(c));
    return new MetadataModel(
      categories,
      data.data.dataSourceProviders,
      data.data.tags.map((t: any) => convertToTag(t))
    );
  }
}

function convertToTag(data: any): Tag {
  return {
    id: data.id,
    slug: data.slug,
    name: data.name,
    type: data.tagType,
  };
}
